import { useToasts } from "@certn/cascadia";
import { CheckboxField } from "@certn/cascadia-react";
import { getPrivacyPolicyUrl, getTermsOfUseUrl } from "@certn/utils";
import { Field } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ClickwrapCheckBoxLink } from "./ClickwrapCheckBoxLink";
import { fetchContracts, signContracts } from "./utils";
import { useUser } from "../../hooks/api/useUser";
import { useLanguage } from "../../hooks/useLanguage";

type ClickwrapCheckBoxTypes = {
  testID?: string;
};

export function ClickwrapCheckBox(props: ClickwrapCheckBoxTypes) {
  const [groupID, setGroupID] = useState<string>("");
  const [titles, setTitles] = useState<string[]>([]);
  const [versionIDs, setVersionIDs] = useState<string[]>([]);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const { user } = useUser();
  const { show } = useToasts();
  const { t } = useTranslation();
  const signerID = user?.id ?? "";

  const { currentLanguageLabel } = useLanguage();
  const privacyPolicyUrl = getPrivacyPolicyUrl(currentLanguageLabel);
  const termsOfUseUrl = getTermsOfUseUrl(currentLanguageLabel);

  useEffect(() => {
    const getContractData = async () => {
      const contractData = await fetchContracts();
      setTitles(
        contractData.contracts.map(
          (contractID: number) =>
            contractData.contract_data[contractID]["title"]
        )
      );
      setVersionIDs(contractData["versions"]);
      setGroupID(contractData["group"].toString());
    };

    try {
      getContractData();
    } catch (e) {
      console.error("Failed to fetch clickwrap contract data.", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  }, []);

  const handleTermsAcceptedToggle = async (value: boolean) => {
    if (value && !termsAccepted) {
      try {
        await signContracts({
          signerID,
          versionID: versionIDs.join(","),
          groupID,
        });
        setTermsAccepted(value);
      } catch (e) {
        console.error("Failed to sign clickwrap contract", e);
        show({
          title: t("Something went wrong, try again"),
          type: "error",
        });
      }
    }
  };

  return (
    <Field
      name="consent_tou_and_privacy"
      as={CheckboxField}
      isRequired
      onSelect={handleTermsAcceptedToggle}
      testID={props.testID}
    >
      <Trans>
        I have read and agree to Certn's{" "}
        <ClickwrapCheckBoxLink title={titles[1]} url={privacyPolicyUrl} /> and{" "}
        <ClickwrapCheckBoxLink title={titles[0]} url={termsOfUseUrl} />.
      </Trans>
    </Field>
  );
}
